/** The cards' active self-lit foil optics, applied independently to three die regions. */
import * as THREE from 'three';
import { FOIL_LIGHTING_DEFAULTS, FOIL_LIGHTING_GLSL } from '../../sorcery/foilLighting.js';
import { getFoilMatcap } from '../../sorcery/foilMaterial.js';
import { DEFAULT_FOIL_PARAMS, paramUniformName } from '../../sorcery/foilParams.js';
import { atlasLayout } from './atlasPainter.js';

export const DEFAULT_DICE_FOIL = {
  artwork: true, mode: -1,
  numerals: false, numeralMode: -1,
  rim: false, rimMode: -1,
  metalTint: FOIL_LIGHTING_DEFAULTS.metalTint,
  iridStr: FOIL_LIGHTING_DEFAULTS.iridStr,
  sparkleStr: FOIL_LIGHTING_DEFAULTS.sparkleStr,
  glintBrightness: FOIL_LIGHTING_DEFAULTS.glintBrightness,
};

const VARYINGS = /* glsl */ `
  varying vec3 vFoilTangent;
  varying vec3 vFoilNormal;
  varying vec2 vFoilUv;
`;
const DICE_FOIL_GLSL = /* glsl */ `
  ${VARYINGS}
  uniform vec2 uDiceAtlas;
  uniform vec3 uDiceRegions;
  uniform vec3 uDiceModes;
  uniform float uDiceFoilEnabled;
  uniform sampler2D uDiceArtwork;
  uniform vec2 uDiceRestLight;
  uniform float uDiceRelief;
  vec3 diceFoilToDisplay(vec3 c) {
    return mix(c * 12.92, 1.055 * pow(max(c, 0.0), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
  }
  vec3 diceFoilToLinear(vec3 c) {
    c = clamp(c, 0.0, 1.0);
    return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
  }
  vec3 diceRegionFoil(vec3 base, float mode, vec2 gradient, vec2 tilt) {
    FoilSurface s;
    s.base = base;
    s.N = normalize(vec3(-gradient * uDiceRelief, 1.0));
    s.V = vec3(0.0, 0.0, 1.0);
    s.lightXY = tilt + uDiceRestLight;
    s.Lr = normalize(vec3(s.lightXY * 2.2, 1.0));
    s.glareGuard = smoothstep(0.0, 0.12, length(s.lightXY));
    s.uv0 = vFoilUv;
    s.puv = s.uv0;
    // Match the card's pattern density, independently of the dice atlas size.
    s.texel = vec2(1.0 / 744.0, 1.0 / 1039.0);
    // Solid ink/rim auto coverage uses its own colour, never the artwork.
    s.foilMode = mode < 0.0 ? (foilLuma(base) < 0.30 ? 0.5 : 0.0) : mode;
    s.shine = mode > 1.5 ? 1.0 : foilCoverage(base, s.foilMode);
    // All includes the white-substrate hologram as well as full coverage.
    if (mode > 1.5) s.foilMode = 0.0;
    float chroma = max(max(base.r, base.g), base.b) - min(min(base.r, base.g), base.b);
    s.whiteMask = smoothstep(0.80, 0.96, foilLuma(base)) * (1.0 - smoothstep(0.08, 0.28, chroma));
    s.relief = clamp(length(gradient) * 7.0, 0.0, 1.0);
    s.zoneFoil = 1.0;
    s.bevel = 0.0;
    s.tilt = tilt;
    s.fromCenter = min(1.0, length(tilt) * 2.0);
    s.sceneLit = 0.0;
    s.depthDelta = 0.0;
    return diceFoilToLinear(shadeFoilSurface(s));
  }
`;

const DICE_FOIL_FRAGMENT = /* glsl */ `
  #if defined(USE_ROUGHNESSMAP) && defined(USE_MAP)
    vec3 foilRegions = texture2D(roughnessMap, vRoughnessMapUv).rgb;
    if (uDiceFoilEnabled > 0.5) {
      float face = step(0.5, foilRegions.b);
      float ink = foilRegions.r * face;
      float groundOn = mix(uDiceRegions.z, uDiceRegions.x, face);
      float inkOn = uDiceRegions.y;
      if (groundOn + inkOn * ink > 0.0) {
        vec3 art = texture2D(uDiceArtwork, vMapUv).rgb;
        vec3 painted = diceFoilToDisplay(texture2D(map, vMapUv).rgb);
        // Canvas composites in display space. Recover the actual ink, including
        // filtered graphite, instead of borrowing the colour underneath it.
        vec3 numeral = clamp((painted - art * (1.0 - ink)) / max(ink, 0.004), 0.0, 1.0);
        vec3 T = normalize(vFoilTangent);
        vec3 B = normalize(cross(normalize(vFoilNormal), T));
        vec3 viewDir = isOrthographic ? vec3(0.0, 0.0, 1.0) : normalize(vViewPosition);
        vec2 tilt = clamp(vec2(dot(viewDir, T), dot(viewDir, B)) * 0.5, -0.5, 0.5);
        // Mild luminance relief, as on the card; the dice's physical grain
        // normal remains available to ordinary regions without driving foil.
        vec2 delta = vec2(2.0 / 744.0) / uDiceAtlas;
        vec2 gradient = vec2(
          foilLuma(texture2D(uDiceArtwork, vMapUv + vec2(delta.x, 0.0)).rgb) - foilLuma(texture2D(uDiceArtwork, vMapUv - vec2(delta.x, 0.0)).rgb),
          foilLuma(texture2D(uDiceArtwork, vMapUv + vec2(0.0, delta.y)).rgb) - foilLuma(texture2D(uDiceArtwork, vMapUv - vec2(0.0, delta.y)).rgb)
        ) * face;
        vec3 artLinear = diceFoilToLinear(art);
        vec3 inkLinear = diceFoilToLinear(numeral);
        vec3 layers = mix(artLinear, inkLinear, ink);
        // Partition the ordinary shaded pixel by each layer's contribution.
        // This keeps disabled regions and avoids applying ink opacity twice
        // at antialiased edges when replacing either or both layers with foil.
        vec3 retained = ((1.0 - ink) * artLinear * (1.0 - groundOn) + ink * inkLinear * (1.0 - inkOn)) / max(layers, vec3(0.00001));
        vec3 foil = vec3(0.0);
        if (groundOn > 0.5) foil += (1.0 - ink) * diceRegionFoil(art, mix(uDiceModes.z, uDiceModes.x, face), gradient, tilt);
        if (inkOn > 0.5 && ink > 0.001) foil += ink * diceRegionFoil(numeral, uDiceModes.y, vec2(0.0), tilt);
        vec3 foilTint = diffuse;
        #if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
          foilTint *= vColor.rgb;
        #endif
        float coverage = (1.0 - ink) * groundOn + ink * inkOn;
        gl_FragColor.rgb = gl_FragColor.rgb * retained + foil * foilTint + totalEmissiveRadiance * coverage;
      }
    }
  #endif
`;

// Keep uniform objects stable: Three may reuse a compiled program without
// calling onBeforeCompile again after a slider or palette change.
const foilUniforms = new WeakMap();
export function configureDieFoil(material, foil, sides) {
  material.userData.diceSides = sides;
  material.userData.diceFoil = foil ? { ...foil } : null;
  let uniforms = foilUniforms.get(material);
  if (!foil && !uniforms) return;
  if (!uniforms) {
    uniforms = Object.fromEntries(Object.entries(FOIL_LIGHTING_DEFAULTS).map(([key, value]) => [paramUniformName(key), { value }]));
    const { cols, rows } = atlasLayout(sides);
    Object.assign(uniforms, {
      uDiceAtlas: { value: new THREE.Vector2(cols, rows) },
      uDiceRegions: { value: new THREE.Vector3() },
      uDiceModes: { value: new THREE.Vector3() },
      uDiceFoilEnabled: { value: 1 },
      uDiceArtwork: { value: null },
      uDiceRestLight: { value: new THREE.Vector2(DEFAULT_FOIL_PARAMS.restLightX, DEFAULT_FOIL_PARAMS.restLightY) },
      uDiceRelief: { value: DEFAULT_FOIL_PARAMS.reliefStrength },
      uMatcap: { value: null },
    });
    foilUniforms.set(material, uniforms);
    material.customProgramCacheKey = () => 'dice-foil-v2';
    material.onBeforeCompile = (shader) => {
      uniforms.uMatcap.value = getFoilMatcap((texture) => { uniforms.uMatcap.value = texture; });
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('void main() {', `${VARYINGS}\nattribute vec3 foilTangent;\nattribute vec2 foilUv;\nvoid main() {`)
        .replace('#include <defaultnormal_vertex>', `#include <defaultnormal_vertex>
          vFoilNormal = normalize(transformedNormal);
          vFoilTangent = normalize(normalMatrix * foilTangent);
          vFoilUv = foilUv;
        `);
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', `${FOIL_LIGHTING_GLSL}\n${DICE_FOIL_GLSL}\nvoid main() {`)
        .replace('#include <colorspace_fragment>', `${DICE_FOIL_FRAGMENT}\n#include <colorspace_fragment>`);
    };
  }
  const f = { ...DEFAULT_DICE_FOIL, ...foil };
  uniforms.uDiceFoilEnabled.value = foil ? 1 : 0;
  uniforms.uDiceRegions.value.set(Number(f.artwork), Number(f.numerals), Number(f.rim));
  uniforms.uDiceModes.value.set(f.mode < 0 ? material.foilArtworkMap?.userData.foilMode ?? 0 : f.mode, f.numeralMode, f.rimMode);
  uniforms.uDiceArtwork.value = material.foilArtworkMap;
  for (const [key, value] of Object.entries(FOIL_LIGHTING_DEFAULTS)) uniforms[paramUniformName(key)].value = foil?.[key] ?? value;
}
